import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { SG_APV3_FETCH_EVENT, SG_APV3_GET_EVENT_RESPONSES, SG_APV3_CLOSE_EVENT, SG_APV3_FETCH_CAMPAIGN, SG_APV3_UPDATE_EVENT, SG_APV3_GET_REMINDER_SCHEDULE, SG_APV3_UPSERT_REMINDER, SG_APV3_DELETE_REMINDER } from "constants/actions";
import { v4 as uuidv4 } from "uuid";
import AskNoraDrawer from "./AskNoraDrawer";

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to { opacity: 1; transform: translateY(0); }
`;
const shimmer = keyframes`
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
`;

const Page = styled.main`
  display: flex; flex-direction: column; gap: 20px;
  padding: 0 24px 64px; max-width: 1200px; margin: 0 auto; min-height: 100vh;
  animation: ${fadeUp} 260ms ease-out both;
`;
const Breadcrumbs = styled.nav`
  display: flex; align-items: center; gap: 8px; padding: 10px 0 0 0; color: #6b7280;
`;
const Crumb = styled.span`
  font-size: 14px; font-weight: ${p => (p.active ? 600 : 500)};
  color: ${p => (p.active ? '#111827' : '#6b7280')}; cursor: ${p => (p.clickable ? 'pointer' : 'default')};
`;
const Separator = styled.span` color: #d1d5db;`;
const Title = styled.h1` margin: 0; font-size: 28px; font-weight: 700;`;
const Panel = styled.section` background: #fff; padding: 16px 0 24px 0;`;
const PanelTitle = styled.h2` margin: 0 0 12px 0; font-size: 18px;`;
const Button = styled.button`
  height: 34px; padding: 0 12px; border-radius: 8px; border: 1px solid #2c6bed;
  background: linear-gradient(90deg, #2c6bed, #6b5cff); color: #fff; font-weight: 600; font-size: 13px; cursor: pointer;
`;
const GhostButton = styled.button`
  height: 34px; padding: 0 12px; border-radius: 8px; border: 1px solid #e5e7eb;
  background: #fff; color: #374151; font-weight: 600; font-size: 13px; cursor: pointer;
`;

// Stats and progress UI
const InfoGrid = styled.div`
  display: grid; grid-template-columns: repeat(6, minmax(140px, 1fr)); gap: 12px;
`;
const StatCard = styled.div`
  border: 1px solid #e7e7e7; border-radius: 10px; background: #fff; padding: 12px;
`;
const StatLabel = styled.div`
  font-size: 12px; color: #6b7280;
`;
const StatNumber = styled.div`
  font-weight: 700; font-size: 18px; color: #111827;
`;
const ProgressBar = styled.div`
  height: 8px; background: #f3f4f6; border-radius: 999px; overflow: hidden; margin-top: 6px;
`;
const ProgressFill = styled.div`
  height: 100%; width: ${p => Math.min(Math.max(p.value || 0, 0), 100)}%; background: linear-gradient(90deg, #6b5cff, #2c6bed);
`;

// Lightweight modal components
const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(17,24,39,0.45); display: grid; place-items: center; z-index: 50;
`;
const ModalCard = styled.div`
  background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 16px; width: 100%; max-width: 520px; box-shadow: 0 10px 20px rgba(0,0,0,0.08);
`;
const ModalTitle = styled.div`
  font-size: 16px; font-weight: 700; color: #111827;
`;
const Actions = styled.div`
  margin-top: 14px; display: flex; justify-content: flex-end; gap: 10px;
`;
const Field = styled.div`
  display: grid; gap: 6px; margin-top: 10px;
`;
const Label = styled.label`
  font-size: 12px; color: #6b7280; font-weight: 600;
`;
const Input = styled.input`
  height: 34px; padding: 0 10px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 13px;
`;
const Textarea = styled.textarea`
  min-height: 110px; padding: 8px 10px; border: 1px solid #e5e7eb; border-radius: 8px; font-size: 13px; resize: vertical;
`;

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
  } catch {
    return '—';
  }
}

function formatDateTime(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const date = new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(d);
    const time = new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit' }).format(d);
    return `${date} at ${time}`;
  } catch {
    return '—';
  }
}

function formatDurationDaysHours(startIso, endIso) {
  try {
    if (!startIso || !endIso) return '—';
    const start = new Date(startIso).getTime();
    const end = new Date(endIso).getTime();
    if (!(isFinite(start) && isFinite(end))) return '—';
    const diffMs = end - start;
    if (diffMs <= 0) return '—';
    const dayMs = 24 * 60 * 60 * 1000;
    const hourMs = 60 * 60 * 1000;
    const days = Math.floor(diffMs / dayMs);
    const hours = Math.floor((diffMs % dayMs) / hourMs);
    const dayLabel = days === 1 ? 'day' : 'days';
    const hourLabel = hours === 1 ? 'hour' : 'hours';
    if (hours === 0) return `${days} ${dayLabel}`;
    return `${days} ${dayLabel}, ${hours} ${hourLabel}`;
  } catch {
    return '—';
  }
}

// Layout similar to survey page: schedule card + right stats stack
const TopGrid = styled.div`
  display: grid; gap: 12px; align-items: start;
  grid-template-columns: 1fr;
  @media (min-width: 980px) { grid-template-columns: 1.7fr 1fr; }
`;
const ScheduleCard = styled.section`
  border: 1px solid #e5e7eb; border-radius: 10px; background: #fff;
`;
const ScheduleHeader = styled.div`
  padding: 12px; border-bottom: 1px solid #f3f4f6; font-weight: 700; color: #111827;
`;
const ScheduleBody = styled.div`
  display: grid; grid-template-columns: 1fr; gap: 16px; padding: 14px 12px;
  @media (min-width: 640px) { grid-template-columns: 1fr 1fr; }
`;
const ScheduleItem = styled.div`display: grid; gap: 6px;`;
const ScheduleLabel = styled.div`font-size: 12px; color: #6b7280; font-weight: 600;`;
const ScheduleValue = styled.div`font-size: 14px; color: #111827; font-weight: 600;`;
const StatsCol = styled.div`display: grid; gap: 12px;`;
const StatTile = styled.div`
  display: grid; place-items: center; gap: 4px;
  border: 1px solid #e5e7eb; border-radius: 10px; background: #fff; padding: 16px 12px;
`;
const StatTileNumber = styled.div`font-size: 28px; font-weight: 800; color: #1f2937;`;
const StatTileLabel = styled.div`font-size: 12px; color: #6b7280; font-weight: 600;`;

// Bottom row grid matching TopGrid columns
const BottomGrid = styled.div`
  display: grid; gap: 12px; grid-template-columns: 1fr;
  @media (min-width: 980px) { grid-template-columns: 1.7fr 1fr; }
`;

const Table = styled.div`
  border: 1px solid #e5e7eb; border-radius: 8px; overflow: hidden; margin-bottom: 28px; background: #fff;
  box-shadow: 0 1px 0 rgba(17,24,39,0.02);
`;
const Row = styled.div`
  display: grid; grid-template-columns: 1fr 0.7fr 0.9fr 0.6fr 0.5fr 0.5fr; gap: 12px; align-items: center; padding: 10px 12px; border-top: 1px solid #f3f4f6;
  font-size: 13px;
  &:first-child { border-top: none; background: #fafafa; font-weight: 600; color: #111827; }
  &:not(:first-child):hover { background: #fbfbff; }
  > div:nth-child(3),
  > div:nth-child(4),
  > div:nth-child(5) { color: #374151; }
  > div:nth-child(4),
  > div:nth-child(5) { justify-self: end; font-variant-numeric: tabular-nums; }
  > div:nth-child(6) { justify-self: end; }
`;

const StatusBadge = styled.span`
  display: inline-flex; align-items: center; gap: 6px; padding: 3px 8px; border-radius: 999px; border: 1px solid #e5e7eb;
  font-size: 12px; font-weight: 700; line-height: 1; white-space: nowrap;
  color: #374151; background: #f3f4f6;
  &[data-variant="completed"] { color: #065f46; background: #ecfdf5; border-color: #a7f3d0; }
  &[data-variant="started"] { color: #1e40af; background: #eff6ff; border-color: #bfdbfe; }
  &[data-variant="invited"] { color: #374151; background: #f3f4f6; border-color: #e5e7eb; }
  &[data-variant="overdue"] { color: #991b1b; background: #fef2f2; border-color: #fecaca; }
`;

const CountPill = styled.span`
  display: inline-grid; place-items: center; min-width: 26px; height: 22px; padding: 0 8px; border-radius: 999px;
  border: 1px solid #e5e7eb; background: #f3f4f6; font-size: 12px; font-weight: 700; color: #374151;
  &[data-active="true"] { color: #1e40af; background: #eff6ff; border-color: #bfdbfe; }
`;

// Skeleton primitives
const SkeletonBox = styled.div`
  position: relative; overflow: hidden; border-radius: 8px; background: #eef2f7; height: ${p => p.h || 12}px;
  &:after { content: ""; position: absolute; inset: 0; background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.7) 50%, rgba(255,255,255,0) 100%); background-size: 200% 100%; animation: ${shimmer} 1200ms ease-in-out infinite; }
`;
const SkeletonCircle = styled.div`
  position: relative; overflow: hidden; border-radius: 999px; background: #eef2f7; width: ${p => p.d || 32}px; height: ${p => p.d || 32}px;
  &:after { content: ""; position: absolute; inset: 0; background: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,.7) 50%, rgba(255,255,255,0) 100%); background-size: 200% 100%; animation: ${shimmer} 1200ms ease-in-out infinite; border-radius: inherit; }
`;

const Reveal = styled.div`
  animation: ${fadeUp} 220ms ease-out both;
`;

export default function EventDetail() {
  const { id, eventId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const event = useSelector(s => s.action_planning_v3?.selectedEvent);
  const campaign = useSelector(s => s.action_planning_v3?.selectedCampaign);
  const responses = useSelector(s => s.action_planning_v3?.eventResponses);
  const loading = useSelector(s => s.action_planning_v3?.loading);
  const reminderSchedule = useSelector(s => s.action_planning_v3?.reminderSchedule || []);

  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closing, setClosing] = useState(false);
  const [toast, setToast] = useState("");

  // Ask Nora state
  const [showAskNora, setShowAskNora] = useState(false);

  // Reminder UI state
  const [showViewReminders, setShowViewReminders] = useState(false);
  const [showScheduleReminder, setShowScheduleReminder] = useState(false);
  const [reminderSubject, setReminderSubject] = useState("");
  const [reminderBody, setReminderBody] = useState("");
  const [reminderWhen, setReminderWhen] = useState("");
  const [reminderAttempted, setReminderAttempted] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [editingReminderKey, setEditingReminderKey] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [pendingDeleteReminder, setPendingDeleteReminder] = useState(null);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

  // Edit Event modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editScheduled, setEditScheduled] = useState("");
  const [editCloseDate, setEditCloseDate] = useState("");
  const [editActions, setEditActions] = useState(1);
  const [editAttempted, setEditAttempted] = useState(false);

  // Normalize responses into a flat array regardless of server shape
  const responseList = useMemo(() => (Array.isArray(responses) ? responses : (responses?.results || [])), [responses]);
  const summary = useMemo(() => responses?.summary || {}, [responses]);

  // Build a full list of managers for this event's campaign and map responses by manager
  const managers = useMemo(() => {
    const fromEvent = Array.isArray(event?.campaign?.selected_managers) ? event.campaign.selected_managers : null;
    if (fromEvent) return fromEvent;
    return Array.isArray(campaign?.selected_managers) ? campaign.selected_managers : [];
  }, [event, campaign]);
  const responsesIndex = useMemo(() => {
    const byId = new Map();
    const byIdStr = new Map();
    const byEmail = new Map();
    (responseList || []).forEach(r => {
      const rid = r?.manager?.id;
      if (rid != null) {
        if (!byId.has(rid)) byId.set(rid, r);
        const ridStr = String(rid);
        if (!byIdStr.has(ridStr)) byIdStr.set(ridStr, r);
      }
      const remail = (r?.manager?.email || '').toLowerCase();
      if (remail && !byEmail.has(remail)) byEmail.set(remail, r);
    });
    return { byId, byIdStr, byEmail };
  }, [responseList]);
  const managerRows = useMemo(() => {
    return managers.map(m => {
      const mid = m?.id;
      const memail = (m?.email || m?.manager?.email || '').toLowerCase();
      const byIdMatch = mid != null ? (responsesIndex.byId.get(mid) || responsesIndex.byIdStr.get(String(mid))) : null;
      const byEmailMatch = memail ? responsesIndex.byEmail.get(memail) : null;
      const resp = byIdMatch || byEmailMatch || null;
      return { manager: m, response: resp };
    });
  }, [managers, responsesIndex]);
  const completedCount = useMemo(() => (responseList || []).filter(r => r?.status === 'completed').length, [responseList]);
  const pendingCount = useMemo(() => Math.max(0, managers.length - completedCount), [managers.length, completedCount]);

  // KPI values
  const invitedCount = useMemo(() => {
    const fromStats = event?.participation_stats?.total_invited;
    if (fromStats != null) return fromStats;
    return Array.isArray(event?.campaign?.selected_managers) ? event.campaign.selected_managers.length : managers.length;
  }, [event, managers]);

  const completionPercent = useMemo(() => {
    const fromStats = event?.participation_stats?.completion_rate;
    if (fromStats != null && !Number.isNaN(Number(fromStats))) return Math.round(Number(fromStats));
    if (invitedCount > 0) return Math.round((completedCount / invitedCount) * 100);
    return 0;
  }, [event, invitedCount, completedCount]);

  const remindersSent = event?.reminder_status?.reminders_sent ?? 0;
  const lastReminder = event?.reminder_status?.last_reminder_sent;

  // Build metrics lookup for per-manager on track percent
  const metricsByManagerId = useMemo(() => {
    try {
      const list = Array.isArray(event?.metrics?.managers) ? event.metrics.managers : [];
      const map = new Map();
      for (const item of list) {
        const raw = item?.manager_id;
        if (raw == null) continue;
        const asNum = Number(raw);
        if (!Number.isNaN(asNum)) map.set(asNum, item);
        map.set(String(raw), item);
      }
      return map;
    } catch { return new Map(); }
  }, [event]);

  const computeOnTrackPercentForManager = (managerId) => {
    try {
      if (managerId == null) return 0;
      const keyNum = Number(managerId);
      const mm = metricsByManagerId.get(keyNum) || metricsByManagerId.get(String(managerId));
      if (!mm) return 0;

      let statusCounts = mm?.status_counts_for_event || {};
      let totalActions = 0;
      if (statusCounts && typeof statusCounts === 'object') {
        totalActions = Object.values(statusCounts).reduce((sum, v) => sum + Number(v || 0), 0);
      }
      if (!totalActions) {
        const arr = Array.isArray(mm?.action_update_statuses_for_event) ? mm.action_update_statuses_for_event : [];
        totalActions = arr.length;
        const counts = {};
        for (const name of arr) counts[name] = (counts[name] || 0) + 1;
        statusCounts = counts;
      }
      if (!totalActions) return 0;

      const onTrackNames = ['Completed', 'On Track', 'In Progress'];
      const onTrackCount = onTrackNames.reduce((sum, name) => sum + Number(statusCounts?.[name] || 0), 0);
      const pct = Math.max(0, Math.min(100, (onTrackCount / totalActions) * 100));
      return Number(pct.toFixed(1));
    } catch { return 0; }
  };

  // Match schedule card height to three tiles column
  const scheduleRef = useRef(null);
  const rightColRef = useRef(null);
  useEffect(() => {
    function syncHeight() {
      try {
        const left = scheduleRef.current;
        const right = rightColRef.current;
        if (!left || !right) return;
        // Reset then set to right column height
        left.style.minHeight = 'unset';
        const h = right.getBoundingClientRect().height;
        left.style.minHeight = `${Math.ceil(h)}px`;
      } catch {}
    }
    syncHeight();
    window.addEventListener('resize', syncHeight);
    const t = setTimeout(syncHeight, 0);
    return () => { window.removeEventListener('resize', syncHeight); clearTimeout(t); };
  }, [invitedCount, completedCount, remindersSent, lastReminder, completionPercent]);

  useEffect(() => {
    if (eventId) {
      dispatch({ type: SG_APV3_FETCH_EVENT, payload: { id: eventId } });
      dispatch({ type: SG_APV3_GET_EVENT_RESPONSES, payload: { id: eventId } });
      dispatch({ type: SG_APV3_GET_REMINDER_SCHEDULE, payload: { id: eventId } });
    }
  }, [dispatch, eventId]);

  // Ensure campaign details (including selected_managers) are loaded
  useEffect(() => {
    if (!id) return;
    if (!campaign?.id || String(campaign.id) !== String(id)) {
      dispatch({ type: SG_APV3_FETCH_CAMPAIGN, payload: { id } });
    }
  }, [id, campaign, dispatch]);

  useEffect(() => {
    if (closing && !loading) {
      setShowCloseModal(false);
      setClosing(false);
      setToast('Event closed successfully.');
      setTimeout(() => setToast(''), 2500);
      if (eventId) {
        dispatch({ type: SG_APV3_FETCH_EVENT, payload: { id: eventId } });
        dispatch({ type: SG_APV3_GET_EVENT_RESPONSES, payload: { id: eventId } });
      }
    }
  }, [closing, loading, dispatch, eventId]);

  const submitClose = () => {
    if (!eventId) return;
    setClosing(true);
    dispatch({ type: SG_APV3_CLOSE_EVENT, payload: { id: eventId, body: { close_reason: 'Response deadline reached', send_completion_summary: true, force_close: false } } });
  };

  const clearReminderForm = () => {
    setReminderSubject("");
    setReminderBody("");
    setReminderWhen("");
    setReminderAttempted(false);
    setEditingReminderKey(null);
  };

  const toIsoUtc = (local) => {
    try {
      // local is like "2025-09-20T10:00" (no Z). Treat as local and convert to UTC ISO with Z
      const d = new Date(local);
      return d.toISOString();
    } catch { return null; }
  };

  const handleSendReminder = () => {
    const missing = [];
    if (!(reminderSubject || '').trim()) missing.push('Subject');
    if (!(reminderBody || '').trim()) missing.push('Email body');
    if (!(reminderWhen || '').trim()) missing.push('Date & time');
    if (missing.length > 0) { setReminderAttempted(true); return; }
    const iso = toIsoUtc(reminderWhen);
    if (!iso) { setReminderAttempted(true); return; }
    setSendingReminder(true);
    // Prefer single-row upsert if available; fall back to PATCH full array
    if (editingReminderKey) {
      dispatch({ type: SG_APV3_UPSERT_REMINDER, payload: { id: eventId, body: { key: editingReminderKey, date: iso, reminder_type: 'email', content: { subject: reminderSubject, body: reminderBody } } } });
    } else {
      // Build new list using existing schedule + new entry with a UUID key
      const newKey = `rem-${uuidv4()}`;
      const current = Array.isArray(reminderSchedule) ? reminderSchedule : [];
      const full = current.map(r => ({ key: r.reminder_key || r.key, date: r.run_at || r.date, reminder_type: r.reminder_type || 'email', content: r.content ?? null }));
      full.push({ key: newKey, date: iso, reminder_type: 'email', content: { subject: reminderSubject, body: reminderBody } });
      dispatch({ type: SG_APV3_UPDATE_EVENT, payload: { id: eventId, body: { reminder_schedule: full } } });
    }
    // After server processes, refresh canonical schedule and event
    setTimeout(() => {
      dispatch({ type: SG_APV3_GET_REMINDER_SCHEDULE, payload: { id: eventId } });
      dispatch({ type: SG_APV3_FETCH_EVENT, payload: { id: eventId } });
      setSendingReminder(false);
      setShowScheduleReminder(false);
      clearReminderForm();
      setToast('Reminder scheduled.');
      setTimeout(() => setToast(''), 2500);
    }, 250);
  };

  const isoToLocalInput = (iso) => {
    try {
      if (!iso) return "";
      const d = new Date(iso);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
    } catch { return ""; }
  };

  const openEditEvent = () => {
    setEditScheduled(isoToLocalInput(event?.scheduled_date));
    setEditCloseDate(isoToLocalInput(event?.close_date));
    const fromSequence = Number(event?.sequence_number);
    const fromConfig = Number(event?.event_config?.response_requirements?.minimum_required_actions);
    const initialActions = (!Number.isNaN(fromSequence) && fromSequence > 0) ? fromSequence : (!Number.isNaN(fromConfig) && fromConfig > 0 ? fromConfig : 1);
    setEditActions(initialActions);
    setEditAttempted(false);
    setShowEditModal(true);
  };

  const canEditStart = useMemo(() => {
    const s = String(event?.status || '').toLowerCase();
    return s === 'scheduled';
  }, [event]);

  const handleSaveEdit = () => {
    const missing = [];
    if (!(editCloseDate || '').trim()) missing.push('End date & time');
    if (canEditStart && !(editScheduled || '').trim()) missing.push('Start date & time');
    if (missing.length > 0) { setEditAttempted(true); return; }

    const body = {};
    if (canEditStart) {
      const isoStart = toIsoUtc(editScheduled);
      if (isoStart) body.scheduled_date = isoStart;
    }
    const isoClose = toIsoUtc(editCloseDate);
    if (isoClose) body.close_date = isoClose;

    if (String(event?.event_type) === 'action_planning') {
      const minActions = Math.max(1, Number(editActions) || 1);
      body.sequence_number = minActions;
      body.event_config = {
        ...(event?.event_config || {}),
        response_requirements: {
          ...(event?.event_config?.response_requirements || {}),
          minimum_required_actions: minActions,
        },
      };
    }

    setSavingEdit(true);
    dispatch({ type: SG_APV3_UPDATE_EVENT, payload: { id: eventId, body } });
  };
 
  const openEditReminder = (row) => {
    setEditingReminderKey(row?.reminder_key || row?.key || null);
    const iso = row?.run_at || row?.date || '';
    try {
      const d = iso ? new Date(iso) : null;
      const local = d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}T${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}` : '';
      setReminderWhen(local);
    } catch { setReminderWhen(''); }
    const content = row?.content || {};
    setReminderSubject(content?.subject || '');
    setReminderBody(content?.body || '');
    setShowScheduleReminder(true);
  };

  const handleDeleteReminder = (row, idx) => {
    if (!eventId) return;
    const reminder_key = row?.reminder_key || row?.key;
    const id = row?.id;
    const reminder_index = typeof idx === 'number' ? idx : undefined;
    if (!id && !reminder_key && typeof reminder_index !== 'number') return;
    dispatch({ type: SG_APV3_DELETE_REMINDER, payload: { id: eventId, body: { id, reminder_key, reminder_index } } });
    setTimeout(() => {
      dispatch({ type: SG_APV3_GET_REMINDER_SCHEDULE, payload: { id: eventId } });
      dispatch({ type: SG_APV3_FETCH_EVENT, payload: { id: eventId } });
      setToast('Reminder deleted.');
      setTimeout(() => setToast(''), 2000);
    }, 200);
  };

  const openDeleteConfirm = (row, idx) => {
    setPendingDeleteReminder(row);
    setPendingDeleteIndex(idx);
    setShowDeleteConfirm(true);
  };

  const isCorrectEvent = useMemo(() => {
    if (!event || !eventId) return false;
    try { return String(event.id) === String(eventId); } catch { return false; }
  }, [event, eventId]);
  const baseLoading = loading || !isCorrectEvent;
  const [skeletonHold, setSkeletonHold] = useState(false);
  const minTimeRef = useRef(0);
  const holdTimerRef = useRef(null);
  useEffect(() => {
    // Enforce minimum 1s skeleton visibility to avoid flashing
    if (baseLoading) {
      minTimeRef.current = Date.now() + 1000;
      setSkeletonHold(true);
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = null;
      }
    } else {
      const remaining = minTimeRef.current - Date.now();
      if (remaining > 0) {
        if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
        holdTimerRef.current = setTimeout(() => {
          setSkeletonHold(false);
          holdTimerRef.current = null;
        }, remaining);
      } else {
        setSkeletonHold(false);
      }
    }
    return () => { if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; } };
  }, [baseLoading]);
  const isLoadingView = baseLoading || skeletonHold;

  // After saving edits, close modal, reset, toast, and refresh event
  useEffect(() => {
    if (savingEdit && !loading) {
      setShowEditModal(false);
      setSavingEdit(false);
      setToast('Event updated.');
      setTimeout(() => setToast(''), 2200);
      if (eventId) {
        dispatch({ type: SG_APV3_FETCH_EVENT, payload: { id: eventId } });
      }
    }
  }, [savingEdit, loading, dispatch, eventId]);

  return (
    <Page>
      <Breadcrumbs>
        <Crumb clickable onClick={() => navigate('/leader/audit/action-plan-v3')}>Action Planning</Crumb>
        <Separator>/</Separator>
        {isLoadingView ? (
          <Crumb><span style={{ display: 'inline-block', width: 140 }}><SkeletonBox h={12} /></span></Crumb>
        ) : (
          <Crumb clickable onClick={() => navigate(`/leader/audit/action-plan-v3/campaign/${id}?tab=events`)}><Reveal>{event?.campaign?.name || campaign?.name || 'Campaign'}</Reveal></Crumb>
        )}
        <Separator>/</Separator>
        {isLoadingView ? (
          <Crumb active><span style={{ display: 'inline-block', width: 120 }}><SkeletonBox h={14} /></span></Crumb>
        ) : (
          <Crumb active><Reveal>{event?.name || 'Event'}</Reveal></Crumb>
        )}
      </Breadcrumbs>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'start', gap: 12 }}>
        <div>
          {isLoadingView ? (
            <>
              <div style={{ width: 240, maxWidth: '60%' }}><SkeletonBox h={28} /></div>
              <div style={{ marginTop: 8, width: 360, maxWidth: '80%' }}><SkeletonBox h={12} /></div>
            </>
          ) : (
            <Reveal>
              <Title>{event?.name || 'Event'}</Title>
              <div style={{ color: '#6b7280', marginTop: 6 }}>{event?.description || '—'}</div>
            </Reveal>
          )}
        </div>
        {!isLoadingView && (
          <div style={{ display: 'flex', gap: 8 }}>
            <GhostButton type="button" onClick={() => setShowAskNora(true)}>Ask Nora</GhostButton>
            <GhostButton type="button" onClick={openEditEvent}>Edit Event</GhostButton>
            {event?.status !== 'completed' && (
              <Button type="button" onClick={() => setShowCloseModal(true)}>Close Event</Button>
            )}
          </div>
        )}
      </div>

      {/* KPI dashboard and actions (responsive) */}
      {isLoadingView ? (
      <TopGrid>
        <ScheduleCard ref={scheduleRef}>
          <ScheduleHeader>Survey Schedule</ScheduleHeader>
          <ScheduleBody>
            <ScheduleItem>
              <ScheduleLabel>Start Date & Time</ScheduleLabel>
              <SkeletonBox h={16} />
            </ScheduleItem>
            <ScheduleItem>
              <ScheduleLabel>End Date & Time</ScheduleLabel>
              <SkeletonBox h={16} />
            </ScheduleItem>
            <ScheduleItem>
              <ScheduleLabel>Timezone</ScheduleLabel>
              <SkeletonBox h={16} />
            </ScheduleItem>
            <ScheduleItem>
              <ScheduleLabel>Event Type</ScheduleLabel>
              <div style={{ width: 120 }}><SkeletonBox h={20} /></div>
            </ScheduleItem>
          </ScheduleBody>
        </ScheduleCard>
        <StatsCol ref={rightColRef}>
          <StatTile>
            <SkeletonBox h={28} />
            <StatTileLabel>Total Recipients</StatTileLabel>
          </StatTile>
          <StatTile>
            <SkeletonBox h={28} />
            <StatTileLabel>Completed</StatTileLabel>
          </StatTile>
          <StatTile>
            <SkeletonBox h={28} />
            <StatTileLabel>Pending</StatTileLabel>
          </StatTile>
        </StatsCol>
      </TopGrid>
      ) : (
      <Reveal>
      <TopGrid>
        <ScheduleCard ref={scheduleRef}>
          <ScheduleHeader>Survey Schedule</ScheduleHeader>
          <ScheduleBody>
            <ScheduleItem>
              <ScheduleLabel>Start Date & Time</ScheduleLabel>
              <ScheduleValue>{formatDateTime(event?.scheduled_date)}</ScheduleValue>
            </ScheduleItem>
            <ScheduleItem>
              <ScheduleLabel>End Date & Time</ScheduleLabel>
              <ScheduleValue>{formatDateTime(event?.close_date)}</ScheduleValue>
            </ScheduleItem>
            <ScheduleItem>
              <ScheduleLabel>Timezone</ScheduleLabel>
              <ScheduleValue>{Intl.DateTimeFormat().resolvedOptions().timeZone || '—'}</ScheduleValue>
            </ScheduleItem>
            <ScheduleItem>
              <ScheduleLabel>Event Type</ScheduleLabel>
              <ScheduleValue style={{ display: 'inline-flex', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: '#6b7280', border: '1px solid #e5e7eb', background: '#f9fafb', borderRadius: 8, padding: '4px 8px' }}>
                  {String(event?.event_type || '').replace('_',' ') || '—'}
                </span>
              </ScheduleValue>
            </ScheduleItem>
          </ScheduleBody>
        </ScheduleCard>
        <StatsCol ref={rightColRef}>
          <StatTile>
            <StatTileNumber>{invitedCount}</StatTileNumber>
            <StatTileLabel>Total Recipients</StatTileLabel>
          </StatTile>
          <StatTile>
            <StatTileNumber>{completedCount}</StatTileNumber>
            <StatTileLabel>Completed</StatTileLabel>
          </StatTile>
          <StatTile>
            <StatTileNumber>{Math.max(invitedCount - completedCount, 0)}</StatTileNumber>
            <StatTileLabel>Pending</StatTileLabel>
          </StatTile>
        </StatsCol>
      </TopGrid>
      </Reveal>
      )}

      {/* Completion + Reminders row */}
      {isLoadingView ? (
      <BottomGrid>
          <StatCard>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center' }}>
              <StatLabel>Completion</StatLabel>
              <div style={{ width: 48 }}><SkeletonBox h={20} /></div>
            </div>
            <ProgressBar>
              <div style={{ height: 8 }}><SkeletonBox h={8} /></div>
            </ProgressBar>
            <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
              <SkeletonBox h={12} />
            </div>
          </StatCard>
          <StatCard>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <StatLabel>Reminders Sent</StatLabel>
                <SkeletonBox h={20} />
              </div>
              <div>
                <StatLabel>Last Reminder</StatLabel>
                <SkeletonBox h={20} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <div style={{ width: 120 }}><SkeletonBox h={34} /></div>
              <div style={{ width: 120 }}><SkeletonBox h={34} /></div>
            </div>
          </StatCard>
      </BottomGrid>
      ) : (
      <Reveal>
      <BottomGrid>
          <StatCard>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center' }}>
              <StatLabel>Completion</StatLabel>
              <StatNumber>{completionPercent}%</StatNumber>
            </div>
            <ProgressBar>
              <ProgressFill value={completionPercent} />
            </ProgressBar>
            <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
              <span>{completedCount} completed of {invitedCount}</span>
            </div>
          </StatCard>
          <StatCard>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <StatLabel>Reminders Sent</StatLabel>
                <StatNumber>{remindersSent}</StatNumber>
              </div>
              <div>
                <StatLabel>Last Reminder</StatLabel>
                <StatNumber>{formatDate(lastReminder)}</StatNumber>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
              <GhostButton type="button" onClick={() => setShowViewReminders(true)}>View Reminders</GhostButton>
              <Button type="button" onClick={() => setShowScheduleReminder(true)}>+ Reminder</Button>
            </div>
          </StatCard>
      </BottomGrid>
      </Reveal>
      )}

      <Panel>
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
          <PanelTitle>Managers</PanelTitle>
        </div>
        <Table>
          <Row>
            <div>Manager</div>
            <div>Status</div>
            <div>Responsiveness</div>
            <div>On track</div>
            <div>Reminders</div>
            <div></div>
          </Row>
          {isLoadingView ? (
            Array.from({ length: 6 }).map((_, idx) => (
              <Row key={`sk-${idx}`}>
                <div><div style={{ width: 180 }}><SkeletonBox h={14} /></div></div>
                <div><div style={{ width: 100 }}><SkeletonBox h={20} /></div></div>
                <div><div style={{ width: 140 }}><SkeletonBox h={14} /></div></div>
                <div><div style={{ width: 40 }}><SkeletonBox h={18} /></div></div>
                <div><div style={{ width: 80 }}><SkeletonBox h={30} /></div></div>
              </Row>
            ))
          ) : (
            <Reveal>
              {managerRows.map(x => {
                const r = x.response;
                const m = x.manager;
                const statusRaw = (r?.status || 'invited');
                const statusLower = String(statusRaw).toLowerCase().replace(/\s+/g, '_');
                const statusVariant = (s => {
                  if (s === 'completed') return 'completed';
                  if (s === 'started' || s === 'in_progress' || s === 'in-progress') return 'started';
                  if (s === 'overdue') return 'overdue';
                  return 'invited';
                })(statusLower);
                const statusLabel = (s => s.charAt(0).toUpperCase() + s.slice(1).replace('_',' '))(statusLower);
                const reminders = Number(r?.reminder_count ?? 0);
                const managerId = m?.id ?? r?.manager?.id ?? null;
                const managerDisplay = m?.full_name || m?.email || `ID ${m?.id}`;
                const onTrackPct = computeOnTrackPercentForManager(managerId);
                return (
                  <Row key={m?.id || m?.email}>
                    <div>{managerDisplay}</div>
                    <div><StatusBadge data-variant={statusVariant}>{statusLabel}</StatusBadge></div>
                    <div>{formatDurationDaysHours(event?.scheduled_date, r?.completed_at)}</div>
                    <div>{onTrackPct}%</div>
                    <div><CountPill data-active={String(reminders > 0)}>{reminders}</CountPill></div>
                    <div>
                      <GhostButton
                        type="button"
                        onClick={() => managerId && navigate(`/leader/audit/action-plan-v3/campaign/${id}/manager/${managerId}`)}
                        title={`View ${managerDisplay}`}
                      >
                        View
                      </GhostButton>
                    </div>
                  </Row>
                );
              })}
              {managerRows.length === 0 && (
                <Row>
                  <div style={{ gridColumn: '1 / -1', color: '#6b7280', fontSize: 13 }}>No managers assigned to this campaign.</div>
                </Row>
              )}
            </Reveal>
          )}
        </Table>
      </Panel>

      {showCloseModal && createPortal(
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, width: '100%', maxWidth: 420, boxShadow: '0 10px 20px rgba(0,0,0,0.08)', maxHeight: '100vh', overflow: 'auto', padding: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Close event</div>
            <div style={{ marginTop: 6, fontSize: 14, color: '#4b5563' }}>Are you sure you want to close this event? Participants will no longer be able to respond.</div>
            <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <GhostButton type="button" onClick={() => setShowCloseModal(false)} disabled={closing}>Cancel</GhostButton>
              <Button type="button" onClick={submitClose} disabled={closing}>{closing ? 'Closing…' : 'Close event'}</Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showViewReminders && createPortal(
        <ModalOverlay onClick={() => setShowViewReminders(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Scheduled Reminders</ModalTitle>
            {Array.isArray(reminderSchedule) && reminderSchedule.length > 0 ? (
              <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                {reminderSchedule.map((r, idx) => (
                  <div key={(r.id || r.reminder_key || r.key)} style={{ display: 'grid', gridTemplateColumns: '1fr auto', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 8, padding: '8px 10px' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{formatDateTime(r.run_at || r.date)}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{r?.content?.subject || 'Email reminder'}</div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <GhostButton type="button" onClick={() => openEditReminder(r)}>Edit</GhostButton>
                      <GhostButton type="button" onClick={() => openDeleteConfirm(r, idx)}>Delete</GhostButton>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ marginTop: 8, fontSize: 14, color: '#4b5563' }}>No scheduled reminders yet.</div>
            )}
            <Actions>
              <GhostButton type="button" onClick={() => setShowViewReminders(false)}>Close</GhostButton>
            </Actions>
          </ModalCard>
        </ModalOverlay>,
        document.body
      )}

      {showScheduleReminder && createPortal(
        <ModalOverlay onClick={() => { setShowScheduleReminder(false); clearReminderForm(); }}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Schedule Reminder</ModalTitle>
            <Field>
              <Label>Subject</Label>
              <Input value={reminderSubject} onChange={(e) => setReminderSubject(e.target.value)} placeholder="Reminder: Action planning due" />
              {reminderAttempted && !(reminderSubject || '').trim() && (
                <div style={{ fontSize: 12, color: '#b91c1c' }}>Subject is required.</div>
              )}
            </Field>
            <Field>
              <Label>Email body</Label>
              <Textarea value={reminderBody} onChange={(e) => setReminderBody(e.target.value)} placeholder="Please remember to complete your action plan..." />
              {reminderAttempted && !(reminderBody || '').trim() && (
                <div style={{ fontSize: 12, color: '#b91c1c' }}>Email body is required.</div>
              )}
            </Field>
            <Field>
              <Label>Date & time</Label>
              <Input type="datetime-local" value={reminderWhen} onChange={(e) => setReminderWhen(e.target.value)} />
              {reminderAttempted && !(reminderWhen || '').trim() && (
                <div style={{ fontSize: 12, color: '#b91c1c' }}>Date & time is required.</div>
              )}
            </Field>
            <Actions>
              <GhostButton type="button" onClick={() => { setShowScheduleReminder(false); clearReminderForm(); }} disabled={sendingReminder}>Cancel</GhostButton>
              <Button type="button" onClick={handleSendReminder} disabled={sendingReminder}>{sendingReminder ? 'Scheduling…' : 'Schedule'}</Button>
            </Actions>
          </ModalCard>
        </ModalOverlay>,
        document.body
      )}

      {showDeleteConfirm && createPortal(
        <ModalOverlay onClick={() => setShowDeleteConfirm(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Delete reminder?</ModalTitle>
            <div style={{ marginTop: 6, fontSize: 14, color: '#4b5563' }}>
              This will remove the scheduled reminder. This action cannot be undone.
            </div>
            <Actions>
              <GhostButton type="button" onClick={() => setShowDeleteConfirm(false)}>Cancel</GhostButton>
              <Button
                type="button"
                onClick={() => {
                  handleDeleteReminder(pendingDeleteReminder, pendingDeleteIndex);
                  setShowDeleteConfirm(false);
                  setPendingDeleteReminder(null);
                  setPendingDeleteIndex(null);
                }}
              >
                Delete
              </Button>
            </Actions>
          </ModalCard>
        </ModalOverlay>,
        document.body
      )}

      {showEditModal && createPortal(
        <ModalOverlay onClick={() => setShowEditModal(false)}>
          <ModalCard onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Edit Event</ModalTitle>
            <Field>
              <Label>Start Date & Time</Label>
              <Input type="datetime-local" value={editScheduled} onChange={(e) => setEditScheduled(e.target.value)} disabled={!canEditStart} />
              {editAttempted && canEditStart && !(editScheduled || '').trim() && (
                <div style={{ fontSize: 12, color: '#b91c1c' }}>Start date & time is required.</div>
              )}
              {!canEditStart && (
                <div style={{ fontSize: 12, color: '#6b7280' }}>Start date can't be edited after sending.</div>
              )}
            </Field>
            <Field>
              <Label>End Date & Time</Label>
              <Input type="datetime-local" value={editCloseDate} onChange={(e) => setEditCloseDate(e.target.value)} />
              {editAttempted && !(editCloseDate || '').trim() && (
                <div style={{ fontSize: 12, color: '#b91c1c' }}>End date & time is required.</div>
              )}
            </Field>
            {String(event?.event_type) === 'action_planning' && (
              <Field>
                <Label># of required actions</Label>
                <select
                  value={String(editActions)}
                  onChange={(e) => setEditActions(Number(e.target.value))}
                  style={{ height: 34, padding: '0 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13 }}
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <option key={i+1} value={i+1}>{i+1}</option>
                  ))}
                </select>
              </Field>
            )}
            <Actions>
              <GhostButton type="button" onClick={() => setShowEditModal(false)} disabled={savingEdit}>Cancel</GhostButton>
              <Button type="button" onClick={handleSaveEdit} disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save'}</Button>
            </Actions>
          </ModalCard>
        </ModalOverlay>,
        document.body
      )}

      {!!toast && (
        <div style={{ position: 'fixed', right: 24, bottom: 20, background: '#111827', color: '#ffffff', borderRadius: 10, border: '1px solid #1f2937', padding: '10px 12px', fontSize: 13, fontWeight: 600, boxShadow: '0 8px 18px rgba(0,0,0,0.15)', zIndex: 50 }}>{toast}</div>
      )}

      <AskNoraDrawer
        open={showAskNora}
        onClose={() => setShowAskNora(false)}
        event={event}
        suggestions={[
          "Which actions are at risk heading into the next check-in?",
          "Who needs follow-up this week and why?",
          "Summarize blockers mentioned across recent updates.",
          "What completion trends should I know about for this event?",
        ]}
        onSend={(q) => {
          // Placeholder send behaviour; integrate with backend/Copilot later
          setToast('Sent to Nora');
          setTimeout(() => setToast(''), 1800);
        }}
      />
    </Page>
  );
}


